import type {
  CarePlan,
  CodeableConcept,
  Practitioner,
  Reference,
  Task,
} from '@medplum/fhirtypes'
import { PRACTITIONER_ROLE } from '#src/codeSystems.ts'
import { invariant } from '#src/invariant.ts'
import type { InitialCarePlan } from '#src/models/care-plans/index.ts'
import type { IDTRole } from '#src/models/care-teams/constants.ts'
import {
  SIGNATURE_TASK_CODES,
  SignatureTaskCode,
} from '#src/models/tasks/constants.ts'
import { SignatureTask } from '#src/models/tasks/signature/signatureTask.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType } from '#src/utils.ts'

export interface CarePlanSignatureTaskParams extends Partial<
  Omit<Task, 'resourceType' | 'requester' | 'owner' | 'performerType'>
> {
  carePlan: InitialCarePlan
  requester: Reference<Practitioner>
  // The IDT seat this signature is for, stored as performerType. Omitted for
  // participant-review signatures.
  role?: IDTRole
  // The practitioner who holds the seat; undefined when unstaffed.
  signer?: Reference<Practitioner>
}

export class CarePlanSignatureTask extends SignatureTask {
  static override get code(): CodeableConcept {
    return SIGNATURE_TASK_CODES[SignatureTaskCode.CARE_PLAN]
  }

  constructor({
    carePlan,
    role,
    signer,
    ...params
  }: CarePlanSignatureTaskParams) {
    super({
      code: CarePlanSignatureTask.code,
      focus: carePlan.reference,
      for: carePlan.patientSubject,
      owner: signer,
      roles: role ? [role] : undefined,
      ...params,
    })
  }

  get carePlan(): Reference<CarePlan> {
    invariant(
      isOfType(this.focus, 'CarePlan'),
      'CarePlanSignatureTask must have a care plan focus'
    )
    return this.focus
  }

  set carePlan(carePlan: Reference<CarePlan>) {
    this.focus = carePlan
  }

  static byCarePlanReference({
    carePlan,
  }: {
    carePlan?: Reference<CarePlan>
  }): QueryTypes {
    return {
      code: this.code,
      focus: carePlan,
      'status:not': 'cancelled',
    }
  }

  static queryOpenByCarePlan({
    carePlan,
  }: {
    carePlan: Reference<CarePlan>
  }): QueryTypes {
    return {
      code: this.code,
      focus: carePlan,
      'status:not': ['completed', 'cancelled', 'entered-in-error'],
    }
  }

  // Care plan signatures are one per IDT seat: the role lives in performerType
  // (a token-searchable Task search param), so a practitioner double-rostered
  // across two roles gets one task per role.
  static byRoles({
    carePlan,
    role,
  }: {
    carePlan: Reference<CarePlan>
    role: IDTRole
  }): QueryTypes {
    return {
      ...this.byCarePlanReference({ carePlan }),
      performer: `${PRACTITIONER_ROLE}|${role}`,
    }
  }

  static searchQuery(): QueryTypes {
    return {
      code: this.code,
    }
  }
}
